
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';

interface AdminNotification {
  id: string;
  type: 'candidature' | 'ticket' | 'ticket_message' | 'societe';
  title: string;
  message: string;
  link: string;
  read: boolean;
  created_at: string;
  metadata?: any;
}

interface AdminStats {
  newCandidatures: number;
  openTickets: number;
  unreadMessages: number;
  pendingSocietes: number;
}

export default function AdminNotificationCenter() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    newCandidatures: 0,
    openTickets: 0,
    unreadMessages: 0,
    pendingSocietes: 0,
  });
  const [loading, setLoading] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  // Fetch admin statistics and notifications
  const fetchAdminData = useCallback(async () => {
    try {
      // Real‑time statistics
      const [candidaturesRes, ticketsRes, societesRes] = await Promise.all([
        // Unprocessed leads (NEW or FORM_SUBMITTED)
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .in('status', ['NEW', 'FORM_SUBMITTED']),

        // Open tickets
        supabase
          .from('tickets')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'OPEN'),

        // Pending societies
        supabase
          .from('societes_recommandees')
          .select('id', { count: 'exact', head: true })
          .eq('statut', 'EN_ATTENTE'),
      ]);

      // Count unread messages on open tickets
      const { data: ticketsWithMessages } = await supabase
        .from('tickets')
        .select('id')
        .eq('status', 'OPEN');

      let unreadMessagesCount = 0;
      if (ticketsWithMessages && ticketsWithMessages.length > 0) {
        const ticketIds = ticketsWithMessages.map((t) => t.id);
        const { count } = await supabase
          .from('ticket_messages')
          .select('id', { count: 'exact', head: true })
          .in('ticket_id', ticketIds)
          .eq('is_admin', false)
          .eq('read_by_admin', false);

        unreadMessagesCount = count || 0;
      }

      setStats({
        newCandidatures: (candidaturesRes as any).count || 0,
        openTickets: (ticketsRes as any).count || 0,
        unreadMessages: unreadMessagesCount,
        pendingSocietes: (societesRes as any).count || 0,
      });

      // Retrieve admin notifications
      const { data: notifs, error: notifsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('target_role', 'ADMIN')
        .order('created_at', { ascending: false })
        .limit(50);

      if (notifsError) {
        console.error('Error fetching admin notifications:', notifsError);
      } else {
        setNotifications(
          (notifs || []).map((n: any) => ({
            id: n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            link: n.link || '',
            read: n.read,
            created_at: n.created_at,
            metadata: n.metadata,
          }))
        );
      }
    } catch (error) {
      console.error('Error retrieving admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark a single notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await supabase.from('notifications').update({ read: true }).eq('id', notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('target_role', 'ADMIN')
        .eq('read', false);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Handle click on a notification
  const handleNotificationClick = (notification: AdminNotification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Polling every 60 seconds
  useEffect(() => {
    fetchAdminData();

    const interval = setInterval(() => {
      fetchAdminData();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchAdminData]);

  // Total unread alerts count
  const totalUnread =
    stats.newCandidatures + stats.openTickets + stats.unreadMessages + stats.pendingSocietes;
  const unreadNotifications = notifications.filter((n) => !n.read).length;

  // Icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'candidature':
        return 'ri-file-list-line';
      case 'ticket':
        return 'ri-customer-service-line';
      case 'ticket_message':
        return 'ri-message-3-line';
      case 'societe':
        return 'ri-building-line';
      default:
        return 'ri-notification-line';
    }
  };

  // Color based on notification type
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'candidature':
        return 'text-blue-400';
      case 'ticket':
        return 'text-orange-400';
      case 'ticket_message':
        return 'text-purple-400';
      case 'societe':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-white hover:bg-[#141824] rounded-lg transition-colors cursor-pointer"
        aria-label="Notifications admin"
      >
        <i className="ri-notification-3-line text-xl"></i>
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#DC143C] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {/* Notification panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-[#0F1115] border border-[#232838] rounded-lg shadow-2xl z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-[#232838]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold text-lg">Centre de notifications</h3>
              {unreadNotifications > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-[#DC143C] hover:text-[#DC143C]/80 font-medium cursor-pointer"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  navigate('/admin/candidatures');
                  setIsOpen(false);
                }}
                className="p-2 bg-[#141824] hover:bg-[#232838] rounded-lg text-left transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-400">Candidatures</div>
                <div className="text-lg font-bold text-white">{stats.newCandidatures}</div>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/support');
                  setIsOpen(false);
                }}
                className="p-2 bg-[#141824] hover:bg-[#232838] rounded-lg text-left transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-400">Tickets ouverts</div>
                <div className="text-lg font-bold text-white">{stats.openTickets}</div>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/support');
                  setIsOpen(false);
                }}
                className="p-2 bg-[#141824] hover:bg-[#232838] rounded-lg text-left transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-400">Messages non lus</div>
                <div className="text-lg font-bold text-white">{stats.unreadMessages}</div>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/societes');
                  setIsOpen(false);
                }}
                className="p-2 bg-[#141824] hover:bg-[#232838] rounded-lg text-left transition-colors cursor-pointer"
              >
                <div className="text-xs text-gray-400">Sociétés en attente</div>
                <div className="text-lg font-bold text-white">{stats.pendingSocietes}</div>
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400">
                <i className="ri-loader-4-line text-2xl animate-spin"></i>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <i className="ri-notification-off-line text-3xl mb-2"></i>
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y divide-[#232838]">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full p-4 text-left hover:bg-[#141824] transition-colors cursor-pointer ${
                      !notification.read ? 'bg-[#141824]/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-lg bg-[#232838] ${getNotificationColor(
                          notification.type
                        )}`}
                      >
                        <i className={`${getNotificationIcon(notification.type)} text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-white font-medium text-sm">{notification.title}</h4>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-[#DC143C] rounded-full flex-shrink-0 mt-1"></span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs mb-1 line-clamp-2">{notification.message}</p>
                        <span className="text-gray-500 text-xs">
                          {formatRelativeTime(notification.created_at)}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-[#232838] text-center">
              <span className="text-xs text-gray-500">
                {unreadNotifications} non lue{unreadNotifications > 1 ? 's' : ''} ·{' '}
                {notifications.length} total
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
